import { NavLink, Navigate, Outlet } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Show, SignOutButton, UserButton } from "@clerk/react";
import { Inbox, Package, FileText, Loader2, ShieldX } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Logo } from "@/components/site/Header";
import { useAdminApi } from "@/lib/api";

const LINKS = [
  { to: "/admin", label: "Inquiries", icon: Inbox, end: true, id: "inquiries" },
  { to: "/admin/products", label: "Availability", icon: Package, id: "products" },
  { to: "/admin/documents", label: "Documents", icon: FileText, id: "documents" },
];

const navCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? "bg-emerald-50 text-emerald-900" : "text-stone-600 hover:bg-stone-100"}`;

const NotAllowed = () => (
  <div className="min-h-screen grid place-items-center bg-stone-100 px-6" data-testid="admin-not-allowed">
    <div className="max-w-md rounded-lg border border-stone-200 bg-white p-10 space-y-4">
      <ShieldX className="h-8 w-8 text-amber-700" />
      <h1 className="text-2xl font-semibold">This account has no admin access</h1>
      <p className="text-sm text-stone-600">Sign in with the SLN Agro Exports owner email to use the admin panel.</p>
      <SignOutButton><button className="rounded-full bg-emerald-900 text-white px-5 py-2.5 text-sm" data-testid="admin-sign-out-btn">Sign out</button></SignOutButton>
    </div>
  </div>
);

const Shell = ({ email }) => (
  <div className="min-h-screen md:flex md:h-screen bg-stone-50 md:overflow-hidden">
    <aside className="md:w-64 bg-white border-b md:border-b-0 md:border-r border-stone-200 flex-shrink-0 flex md:flex-col">
      <div className="p-5 hidden md:block"><Logo /></div>
      <nav className="flex md:flex-col gap-1 p-3 md:p-4 flex-1 overflow-x-auto">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} className={navCls} data-testid={`admin-nav-${l.id}`}>
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 flex items-center gap-3 md:border-t border-stone-200">
        <UserButton />
        <span className="text-xs text-stone-500 truncate hidden md:block" data-testid="admin-email">{email}</span>
      </div>
    </aside>
    <main className="flex-1 overflow-y-auto p-5 md:p-8"><Outlet /></main>
  </div>
);

const Gate = () => {
  const call = useAdminApi();
  const { data, isLoading, error } = useQuery({ queryKey: ["admin-me"], retry: false, queryFn: () => call({ url: "/admin/me" }) });
  if (isLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-800" /></div>;
  if (error?.response?.status === 403) return <NotAllowed />;
  if (error) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="admin-error">
        <div className="text-center space-y-3">
          <p className="text-stone-600">Could not load the admin panel.</p>
          <button onClick={() => window.location.reload()} className="rounded-full bg-emerald-900 text-white px-5 py-2 text-sm" data-testid="admin-retry-btn">Retry</button>
        </div>
      </div>
    );
  }
  return <Shell email={data.email} />;
};

export default function AdminLayout() {
  return (
    <>
      <Seo title="Admin" noindex />
      <Show when="signed-in"><Gate /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
